import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/api';
import { generateRandomToken } from '../utils/stinggenerator';
import { reloadPage } from '../utils/deferredNavigation';
import { getPracticeId } from '../config/practice';
import UserSheet, { PRIV_PAGES, privLabel } from './UserSheet';
import { lastActive } from './userui';
import { errorText as deptErrorText, pickerChanged, pickerState, setUserDepartment } from './userDepartments';
import {
  errorText, fetchCatalog, fetchUserPrivileges, initialSelection, parsePermissions, profileChanged, profileErrors,
  roleTemplateIds, saveUserPrivileges, sheetGroups,
} from './privileges/privilegeModel';
import './user.css';

const dropdowntypes = ["Hourly", "Contractors", "Salaried"];

/* Edit user (Teja's sheet, 10-28) + the privileges revamp (owner 11-03):
   - Details: Location access first (v2 PUT users/{id}/department), then the profile.
   - Privileges: the v2 catalog's areas (plain labels, descriptions, Retired read-only), saved as add / remove
     through v2 PUT users/{id}/privileges - retired, turned-off and unknown ids are kept. If the catalog cannot
     load, the tab shows the v1 rows read-only and nothing about privileges is sent.
   - Changing Role never changes the ticks; "Apply <role> template" previews first.
   - Profile goes to v1 PUT /user only when a profile field changed, never with permissions; email / phone are
     checked only when edited (a stored non-10-digit phone never blocks a privileges or location save).
   - Every failure shows in the sheet, which stays open until everything saved. Service accounts: read-only. */
const EditUser = ({ CloseEditUser, onFileChange, userrow, fetchUsersData, img, deptEnabled = false, department = null, deptOptions = [] }) => {
  const practiseid = getPracticeId();
  const deptInitial = pickerState(department);
  const [deptPick, setDeptPick] = useState(deptInitial);
  const [usersData, setUserData] = useState({});
  const [form, setForm] = useState({ firstname: '', lastname: '', username: '', email: '', phone: '', status: '', rolename: '' });
  const [errors, setErrors] = useState({});
  const [saveErrors, setSaveErrors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [rolesData, setRolesData] = useState([]);
  const [v1Privileges, setV1Privileges] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [catalogFailed, setCatalogFailed] = useState(false);
  const [userPrivs, setUserPrivs] = useState(null);
  const [selected, setSelected] = useState([]);
  const [initialSel, setInitialSel] = useState([]);
  const myemp_type = userrow?.config?.emp_type || '';
  const [emptype, setemptype] = useState(myemp_type);

  useEffect(() => { setemptype(userrow?.config?.emp_type || ''); }, [userrow]);

  useEffect(() => {
    let live = true;
    api.get(`/v1/${practiseid}/roles/get`)
      .then((r) => { if (live) setRolesData(r?.status === 200 && Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (live) setRolesData([]); });
    api.get(`/v1/${practiseid}/user/${userrow.id}`)
      .then((r) => { if (live) setUserData(r?.status === 200 && Array.isArray(r.data) && r.data[0] ? r.data[0] : {}); })
      .catch(() => { if (live) setSaveErrors(['Could not load this user.']); });
    Promise.all([fetchCatalog(), fetchUserPrivileges(userrow.id)])
      .then(([cat, up]) => {
        if (!live) return;
        if (cat?.status !== 200 || !Array.isArray(cat.privileges) || up?.status !== 200) throw new Error('no catalog');
        setCatalog(cat);
        setUserPrivs(up);
        const sel = [...initialSelection(cat, up.permissions)];
        setSelected(sel);
        setInitialSel(sel);
      })
      .catch(() => {
        if (!live) return;
        setCatalogFailed(true);
        // read-only fallback: the v1 rows by page, ticked from the row's own list (null-safe)
        api.get(`/v1/${practiseid}/privileges/get`)
          .then((r) => { if (live) setV1Privileges(r?.status === 200 && Array.isArray(r.data) ? r.data : []); })
          .catch(() => {});
        setSelected(parsePermissions(userrow?.permissions));
      });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userrow?.id]);

  const serviceAccount = !!(userPrivs?.service_account || department?.service_account);
  const readOnlyPrivs = serviceAccount || !catalog;

  const initialProfile = useMemo(() => ({
    firstname: usersData.firstname || '', lastname: usersData.lastname || '', username: usersData.username || '',
    email: usersData.email || '', phone: usersData.phone || '', status: usersData.status === 'A' ? 'A' : 'I',
    rolename: usersData.rolename || '',
  }), [usersData]);
  useEffect(() => { if (Object.keys(usersData).length > 0) setForm(initialProfile); }, [usersData, initialProfile]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const roleOf = (name) => rolesData.find((r) => r.role === name);
  const roleObj = roleOf(form.rolename);
  const groups = useMemo(
    () => sheetGroups({ catalog, privileges: v1Privileges, pages: PRIV_PAGES, privLabel }),
    [catalog, v1Privileges]);
  const template = roleTemplateIds({ catalog, privileges: v1Privileges, role: roleObj });

  const handlePrivilegeChange = (e) => {
    if (readOnlyPrivs) return;
    const { value, checked } = e.target;
    setSelected((prev) => (checked ? [...prev.filter((id) => id !== value), value] : prev.filter((id) => id !== value)));
  };

  const handleEditUser = async () => {
    const profileNow = { ...form };
    const errs = profileErrors(initialProfile, profileNow);
    if (!profileNow.firstname.trim() && initialProfile.firstname) errs.firstName = 'First name is required.';
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSaving(true);
    const failures = [];
    const withGrole = (p) => ({ ...p, grole: roleOf(p.rolename)?.id ?? usersData.grole });
    const profileDirty = Object.keys(usersData).length > 0 && profileChanged(withGrole(initialProfile), withGrole(profileNow));
    try {
      if (profileDirty) {
        try {
          const res = await api.put(`/v1/${practiseid}/user`, {
            rowid: userrow.id, practiceid: practiseid, firstname: profileNow.firstname, lastname: profileNow.lastname,
            email: profileNow.email, username: profileNow.username, phone: profileNow.phone,
            grole: roleObj?.id || usersData.grole, status: profileNow.status, token: generateRandomToken(),
          });
          if (res?.status !== 200) failures.push(`Details: ${res?.message || 'not saved.'}`);
        } catch (err) {
          failures.push(`Details: ${errorText(err, 'not saved.')}`);
        }
      }
      if (emptype !== myemp_type) {
        try {
          await api.put(`/v1/${practiseid}/user/config`, { rowid: userrow.id, emp_type: emptype });
        } catch (err) {
          failures.push(`Employee type: ${errorText(err, 'not saved.')}`);
        }
      }
      if (!readOnlyPrivs) {
        const add = selected.filter((id) => !initialSel.includes(id)).sort((a, b) => a - b);
        const remove = initialSel.filter((id) => !selected.includes(id)).sort((a, b) => a - b);
        if (add.length || remove.length) {
          try {
            const res = await saveUserPrivileges(userrow.id, { add, remove });
            if (res?.status !== 200) failures.push(`Privileges: ${res?.message || 'not saved.'}`);
            else setInitialSel([...selected]);
          } catch (err) {
            failures.push(`Privileges: ${errorText(err, 'not saved.')}`);
          }
        }
      }
      if (deptEnabled && !serviceAccount && pickerChanged(deptInitial, deptPick)) {
        try {
          await setUserDepartment(userrow.id, deptPick);
        } catch (err) {
          failures.push(`Location: ${deptErrorText(err)}`);
        }
      }
    } finally {
      setSaving(false);
    }
    setSaveErrors(failures);
    fetchUsersData();
    if (failures.length) return; // whatever saved is saved; the sheet stays open for the rest
    CloseEditUser();
    // Never mid-recording: the reload would end it (utils/deferredNavigation).
    reloadPage({ reason: "user", subject: "User saved" });
  };

  const initials = `${form.firstname.trim().charAt(0)}${form.lastname.trim().charAt(0)}`.toUpperCase();
  // "bmadmin · last active 5 min ago"; a date or "Never" reads on its own.
  const when = lastActive(userrow?.last_login);
  const seen = when.none
    ? "never signed in"
    : /ago|now|Yesterday/.test(when.text)
      ? `last active ${when.text.charAt(0).toLowerCase()}${when.text.slice(1)}`
      : `last active ${when.text}`;
  const unknownCount = (userPrivs?.unknown_held || []).length;
  const privNote = catalogFailed
    ? 'The privilege list could not be loaded, so privileges are shown read-only and will not be changed. Details and location still save.'
    : unknownCount > 0
      ? `This user also carries ${unknownCount} old privilege id${unknownCount === 1 ? '' : 's'} that no longer exist here; they are kept.`
      : null;

  return (
    <UserSheet
      title={`${form.firstname} ${form.lastname}`.trim() || userrow?.username || "Edit user"}
      sub={[form.username || userrow?.username, seen].filter(Boolean).join(" · ")}
      initials={initials}
      photo={img ? `data:image/jpeg;base64,${img}` : null}
      onPhoto={(e) => onFileChange({ file: e.target.files[0], id: userrow.id })}
      onClose={CloseEditUser}
      error={saveErrors}
      groups={groups}
      held={userPrivs ? parsePermissions(userPrivs.permissions) : parsePermissions(userrow?.permissions)}
      selected={selected}
      setSelected={setSelected}
      onToggle={handlePrivilegeChange}
      roleName={form.rolename}
      template={template}
      keepIds={(catalog?.users_privilege_ids || []).map(String)}
      readOnly={readOnlyPrivs}
      privNote={privNote}
      banner={serviceAccount ? 'Service account: its privileges and location are managed by IT and are shown read-only.' : null}
      details={
        <>
          {deptEnabled && (
            <div className="bmuf-f bmuf-loc" data-testid="edituser-department">
              <label htmlFor="userdept">Location access</label>
              <select
                id="userdept"
                value={deptPick.departmentid}
                disabled={serviceAccount}
                onChange={(e) => setDeptPick((p) => ({ ...p, departmentid: e.target.value }))}
              >
                <option value="">{deptInitial.departmentid ? 'Select department' : 'Not set'}</option>
                {deptOptions.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              <label className="bmuf-check">
                <input
                  type="checkbox"
                  checked={deptPick.allDepartments}
                  disabled={serviceAccount || !deptPick.departmentid}
                  onChange={(e) => setDeptPick((p) => ({ ...p, allDepartments: e.target.checked }))}
                />
                Can view all departments (the one above is home)
              </label>
            </div>
          )}
          <div className="bmuf-grid">
            <div className={`bmuf-f${errors.firstName ? " bad" : ""}`}>
              <label htmlFor="bmuf-first">First name <span className="req">*</span></label>
              <input id="bmuf-first" type="text" placeholder="e.g. Bindu" value={form.firstname} onChange={set('firstname')} disabled={serviceAccount} />
              {errors.firstName && <p className="msg">{errors.firstName}</p>}
            </div>
            <div className="bmuf-f">
              <label htmlFor="bmuf-last">Last name <span className="req">*</span></label>
              <input id="bmuf-last" type="text" placeholder="e.g. Pang" value={form.lastname} onChange={set('lastname')} disabled={serviceAccount} />
            </div>
            <div className="bmuf-f">
              <label htmlFor="bmuf-uid">User ID <span className="req">*</span></label>
              <input id="bmuf-uid" type="text" placeholder="e.g. bindupang" value={form.username} onChange={set('username')} disabled={serviceAccount} />
            </div>
            <div className={`bmuf-f${errors.email ? " bad" : ""}`}>
              <label htmlFor="bmuf-email">Email <span className="req">*</span></label>
              <input id="bmuf-email" type="email" placeholder="name@clinic.org" value={form.email} onChange={set('email')} disabled={serviceAccount} />
              {errors.email && <p className="msg">{errors.email}</p>}
            </div>
            {/* Checked only when edited: 6 logins have a stored phone that is not 10 digits. */}
            <div className={`bmuf-f${errors.phone ? " bad" : ""}`}>
              <label htmlFor="bmuf-phone">Phone</label>
              <input id="bmuf-phone" type="tel" placeholder="10 digits, e.g. 4325550100" value={form.phone} onChange={set('phone')} disabled={serviceAccount} />
              {errors.phone && <p className="msg">{errors.phone}</p>}
            </div>
            <div className="bmuf-f">
              <label htmlFor="bmuf-status">Status</label>
              <select id="bmuf-status" value={form.status} onChange={set('status')} disabled={serviceAccount}>
                <option value="">Select status</option>
                <option value="A">Active</option>
                <option value="I">Inactive</option>
              </select>
            </div>
            <div className="bmuf-f">
              <label htmlFor="bmuf-role">Role</label>
              <select id="bmuf-role" value={form.rolename} onChange={set('rolename')} disabled={serviceAccount}>
                <option value="">Select a role</option>
                {rolesData.map((role) => (
                  <option key={role.id} value={role.role}>{role.role}</option>
                ))}
              </select>
            </div>
            <div className="bmuf-f">
              <label htmlFor="emptype">Employee type</label>
              <select id="emptype" value={emptype} onChange={(e) => setemptype(e.target.value)} disabled={serviceAccount}>
                <option value="">Select type</option>
                {dropdowntypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
        </>
      }
      footer={
        <>
          <button type="button" className="bmuf-cancel" onClick={CloseEditUser}>
            {serviceAccount ? 'Close' : 'Cancel'}
          </button>
          {!serviceAccount && (
            <button type="button" className="bmuf-save" disabled={saving} onClick={handleEditUser}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          )}
        </>
      }
    />
  );
};

export default EditUser;
